import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import * as icons from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Box from '~/common/components/Box';

type IconProps = {
  iconType?: string | number;
  sx?: object;
  fontSize?: string;
  lineHeight?: string;
  color?: string;
  bgColor?: string;
  variant?: 'outline' | 'solid' | 'ghost' | 'round';
};

const isIconDefinition = (value: unknown): value is IconDefinition =>
  typeof value === 'object' && value !== null && 'iconName' in value;

const getIcon = (iconType?: string | number): IconDefinition => {
  const icon = Object.entries(icons).find(([name]) => name === iconType)?.[1];

  return isIconDefinition(icon) ? icon : icons.faCircleQuestion;
};

export default function Icon({
  iconType,
  variant = 'ghost',
  fontSize,
  lineHeight,
  color,
  bgColor,
  sx,
}: Readonly<IconProps>) {
  const icon = getIcon(iconType);
  const iconColor = color ?? 'uwmLogo.blue';
  const iconLineHeight =
    variant === 'round' ? (lineHeight ?? '1.6rem') : (lineHeight ?? '1.3rem');

  let styles = {
    sx,
    color: iconColor,
    bgColor: undefined as string | undefined,
    borderColor: undefined as string | undefined,
    textAlign: 'center' as const,
    width: '1.3rem',
    height: '1.3rem',
    borderRadius: '3px',
    alignSelf: 'center',
    h: undefined as string | undefined,
    w: undefined as string | undefined,
    minW: undefined as string | undefined,
  };

  if (variant === 'solid') {
    styles = {
      ...styles,
      color: 'white',
      bgColor: bgColor ?? iconColor,
      borderColor: iconColor,
    };
  } else if (variant === 'outline') {
    styles = {
      ...styles,
      borderColor: iconColor,
    };
  } else if (variant === 'round') {
    styles = {
      ...styles,
      color: iconColor,
      bgColor: bgColor,
      borderRadius: '1rem',
      h: '1.6rem',
      w: '1.6rem',
      minW: '1.6rem',
    };
  }

  return (
    <Box {...styles} lineHeight={iconLineHeight}>
      <FontAwesomeIcon icon={icon} fontSize={fontSize || 'sm'} />
    </Box>
  );
}
